<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import EmojiPicker from 'vue3-emoji-picker'
import 'vue3-emoji-picker/css'
import { formatFileSize, getFileIcon, type MessageWithReply, useChatStore } from '../../chatModules'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import { useEmojiPicker } from '@/modules/chat-matrix-fast/composables/useEmojiPicker'
import { useFilePreviewUrls } from '@/modules/chat-matrix-fast/composables/useFilePreviewUrls'
import { useSelectedFiles } from '@/modules/chat-matrix-fast/composables/useSelectedFiles'
import { CHAT_FLOATING_Z_INDEX } from '@/modules/chat-matrix-fast/constants/ui'
import { debugConsoleError } from '@/modules/chat-matrix-fast/utils/chatDebugLog'

interface Props {
  modelValue: boolean
  /** Файлы при открытии (Ctrl+V) — без автозапуска file picker */
  initialFiles?: File[]
  replyTo?: {
    id: number | string
    message: MessageWithReply
    senderName?: string
  } | null
}

interface Emit {
  (e: 'update:modelValue', value: boolean): void
  (e: 'messageSent'): void
  (e: 'consumed-initial-files'): void
}

const props = withDefaults(defineProps<Props>(), {
  initialFiles: () => [],
  replyTo: null,
})
const emit = defineEmits<Emit>()

const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

const store = useChatStore()

const comment = ref('')
const isUploading = ref(false)

const {
  selectedFiles,
  selectedFilesCount,
  canAddMoreFiles,
  sortedFiles,
  addFiles,
  removeFileFromSorted: removeSortedFile,
  setFiles,
  resetFiles,
  isImageFile,
  isVideoFile,
  isMediaPreviewFile,
} = useSelectedFiles()

const { getPreviewUrl, getVideoPosterUrl, revokePreviewUrl, revokeAllPreviewUrls }
  = useFilePreviewUrls(selectedFiles)

const emojiBtnRef = ref<HTMLElement | null>(null)
const commentInputRef = ref<HTMLElement | null>(null)
const emojiPicker = useEmojiPicker(emojiBtnRef, commentInputRef)

const removeFileFromSorted = (sortedIndex: number) => {
  const removed = removeSortedFile(sortedIndex)
  if (removed) {
    revokePreviewUrl(removed)
  }
}

const focusCommentInput = () => {
  nextTick(() => {
    const input = commentInputRef.value?.$el?.querySelector('input') as HTMLInputElement | null
    input?.focus()
  })
}

const pickFiles = () => {
  addFiles(focusCommentInput)
}

watch(isOpen, visible => {
  if (!visible) {
    revokeAllPreviewUrls(selectedFiles.value)
    resetFiles()
    comment.value = ''
    emojiPicker.close()
    return
  }

  if (props.initialFiles.length > 0) {
    setFiles(props.initialFiles)
    emit('consumed-initial-files')
    focusCommentInput()
    return
  }

  if (selectedFiles.value.length === 0) {
    nextTick(() => {
      pickFiles()
    })
  }
})

const onEnterKeydown = (e: KeyboardEvent) => {
  if (e.key !== 'Enter' || e.shiftKey || isUploading.value) return
  if (emojiPicker.showEmojiPicker.value) return

  e.preventDefault()
  e.stopPropagation()
  void sendMessage()
}

const sendMessage = async () => {
  if (!store.activeChat?.chat?.id) {
    return
  }

  if (selectedFiles.value.length === 0 && !comment.value.trim()) {
    return
  }

  isUploading.value = true

  try {
    const activeReply = props.replyTo
    let replyPayload: MessageWithReply | undefined = activeReply?.message
    if (replyPayload && activeReply?.senderName) {
      replyPayload = { ...replyPayload, senderName: activeReply.senderName }
    }

    await store.sendMessageWithFiles(
      selectedFiles.value,
      comment.value.trim(),
      activeReply?.id,
      replyPayload,
    )

    revokeAllPreviewUrls(selectedFiles.value)
    selectedFiles.value = []
    comment.value = ''

    isOpen.value = false
    emit('messageSent')
  } catch (error) {
    debugConsoleError('Error sending message:', error)
  } finally {
    isUploading.value = false
  }
}

const closeDialog = () => {
  isOpen.value = false
}

const selectEmoji = (emoji: any) => {
  emojiPicker.onSelectEmoji(emoji, comment)
}

onMounted(() => {
  document.addEventListener('click', emojiPicker.onDocumentClick)
})

onUnmounted(() => {
  document.removeEventListener('click', emojiPicker.onDocumentClick)
})
</script>

<template>
  <VDialog
    v-model="isOpen"
    :z-index="CHAT_FLOATING_Z_INDEX + 1"
    max-width="532"
    persistent
  >
    <VCard
      class="file-upload-dialog pa-6"
      @keydown.enter.capture="onEnterKeydown"
    >
      <div class="d-flex justify-space-between align-center mb-6">
        <h5 class="text-h4">
          Отправить файл
        </h5>
        <CustomCloseButton :close-action="closeDialog" />
      </div>

      <div class="pb-4 pr-5-5">
        <p class="text-body-2 text-medium-emphasis mb-0">
          Выбрано: {{ selectedFilesCount }}
        </p>
      </div>

      <div class="pb-4">
        <div class="selected-files-list pr-5-5">
          <div
            v-for="(file, index) in sortedFiles"
            :key="index"
            class="file-item"
            :class="[{ 'file-item--image': isMediaPreviewFile(file) }]"
          >
            <div class="file-preview">
              <img
                v-if="isImageFile(file) && getPreviewUrl(file)"
                :src="getPreviewUrl(file) || ''"
                :alt="file.name"
                class="file-image-preview"
              >
              <img
                v-else-if="isVideoFile(file) && getVideoPosterUrl(file)"
                :src="getVideoPosterUrl(file) || ''"
                :alt="file.name"
                class="file-image-preview"
              >
              <div
                v-else-if="isVideoFile(file)"
                class="file-video-preview file-video-preview--pending"
              >
                <VIcon
                  icon="ri-video-line"
                  size="28"
                  color="white"
                />
              </div>
              <div
                v-else
                class="file-icon"
              >
                <VIcon
                  :icon="getFileIcon(file.name)"
                  size="20"
                />
              </div>
            </div>

            <div class="file-info">
              <div class="file-name">
                {{ file.name }}
              </div>
              <div
                v-if="!isMediaPreviewFile(file)"
                class="file-size text-caption"
              >
                {{ formatFileSize(file.size) }}
              </div>
            </div>

            <IconBtn
              size="small"
              class="remove-file-btn"
              @click="removeFileFromSorted(index)"
            >
              <VIcon
                icon="ri-close-line"
                size="16"
              />
            </IconBtn>
          </div>
        </div>
      </div>

      <div class="pb-3 pr-5-5">
        <VTextField
          ref="commentInputRef"
          v-model="comment"
          variant="outlined"
          persistent-placeholder
        >
          <template #label>
            <span>Сообщение</span>
          </template>

          <template #append-inner>
            <IconBtn
              ref="emojiBtnRef"
              size="small"
              @click="emojiPicker.toggle"
            >
              <VIcon
                icon="ri-emotion-line"
                size="16"
              />
            </IconBtn>
          </template>
        </VTextField>
      </div>

      <div class="d-flex align-center pr-5-5">
        <VBtn
          variant="outlined"
          color="primary"
          prepend-icon="ri-add-line"
          :disabled="!canAddMoreFiles"
          @click="pickFiles"
        >
          Добавить
        </VBtn>

        <VSpacer />

        <VBtn
          variant="flat"
          color="primary"
          class="px-6"
          append-icon="ri-send-plane-line"
          :loading="isUploading"
          @click="sendMessage"
        >
          Отправить
        </VBtn>
      </div>
    </VCard>

    <Teleport to="body">
      <div
        v-if="emojiPicker.showEmojiPicker.value"
        :ref="(el) => (emojiPicker.emojiPanelRef.value = el as HTMLElement)"
        class="emoji-picker-overlay"
        :style="{
          top: `${emojiPicker.emojiPickerPosition.value.top}px`,
          left: `${emojiPicker.emojiPickerPosition.value.left}px`,
          zIndex: CHAT_FLOATING_Z_INDEX + 2,
          visibility: emojiPicker.isPositioned.value ? 'visible' : 'hidden',
        }"
        @mouseenter="emojiPicker.onPanelEnter"
        @mouseleave="emojiPicker.onPanelLeave"
      >
        <EmojiPicker
          native
          :group-names="emojiPicker.emojiGroupNamesRu"
          :static-texts="emojiPicker.emojiStaticTextsRu"
          @select="selectEmoji"
        />
      </div>
    </Teleport>
  </VDialog>
</template>

<style lang="scss" scoped>
.v-card {
  padding: 24px 10px 32px 32px !important;
  .pr-5-5 {
    padding-right: 22px;
   }
}

.file-upload-dialog {
  border-radius: 12px;
}

.selected-files-list {
  max-height: 300px;
  overflow-y: auto;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 0;
  margin-bottom: 12px;
  background-color: rgba(var(--v-theme-primary), 0.08);
  border-radius: 6px;
  transition: background-color 0.2s ease;
  min-height: 42px;
  height: 42px;

  &.file-item--image {
    min-height: 132px;
    height: 132px;
    align-items: center;
    padding: 8px 12px;
  }

  &:not(.file-item--image) {
    padding: 0 12px;
  }

  &:hover {
    background-color: rgba(var(--v-theme-primary), 0.12);
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.file-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  height: auto;
  margin-right: 12px;
  flex-shrink: 0;

  .file-item--image & {
    width: 116px;
    height: 116px;
    border-radius: 6px;
    overflow: hidden;
    background-color: rgba(var(--v-theme-on-surface), 0.06);
  }
}

.file-image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.file-video-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background-color: #000;

  &--pending {
    display: flex;
    align-items: center;
    justify-content: center;
    background:
      radial-gradient(circle at 35% 30%, rgba(255, 255, 255, 0.12), transparent 50%),
      #1a1a1a;
  }
}

.file-icon {
  position: relative;
  width: auto;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.file-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .file-item--image & {
    justify-content: center;
    padding-top: 0;
  }
}

.file-name {
  font-weight: 500;
  margin-bottom: 4px;
  word-break: break-word;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: 13px;
  line-height: 1.4;

  .file-item--image & {
    margin-bottom: 0;
  }
}

.file-size {
  font-size: 12px;
  line-height: 1.2;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.remove-file-btn {
  margin-left: auto;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  flex-shrink: 0;

  &:hover {
    color: rgb(var(--v-theme-primary));

    :deep(.v-btn__overlay),
    :deep(.v-btn__underlay) {
      opacity: 0 !important;
    }
  }
}

.selected-files-list::-webkit-scrollbar {
  width: 6px;
}

.selected-files-list::-webkit-scrollbar-track {
  background: rgba(var(--v-theme-on-surface), 0.06);
  border-radius: 3px;
}

.selected-files-list::-webkit-scrollbar-thumb {
  background: rgba(var(--v-theme-on-surface), 0.2);
  border-radius: 3px;
}

.selected-files-list::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--v-theme-on-surface), 0.3);
}

.v-card-title {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-weight: 600;
}
</style>

<style lang="scss">
.emoji-picker-overlay {
  position: fixed;
  z-index: 9999;
  background: rgb(var(--v-theme-surface));
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
</style>
